//#region IMPORT
import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Dialog } from '../MUIs';
import { AgGridReact } from 'ag-grid-react';
import {
  Alert,
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import { Build as BuildIcon, Close as CloseIcon } from '@mui/icons-material';
import GridFunctions from '../../Services/agGrid/ag-Grid-functions';
import { apiRequest } from '../../Services/Common/ApiRequest';
import Global from '../../Services/Common/Global';
import { mapParamsBySchema } from '../../util';
import { GON_BASIC_Schema } from '../../Scripts/BasicInfo/Gongjong/GON_BASIC/GON_BASIC_Schema';
import { CreateColumn } from '../../Services/agGrid/createColumn';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
//#endregion

const depColumnDefs = [
  CreateColumn('depNmLast', '부서', 'left', 200, false, false, false, 'text', {
    flex: 1,
    minWidth: 150,
  }),
  CreateColumn('depCd', 'depCd', 'left', 0, false, false, true, 'text'),
];

const gongjongColumnDefs = [
  CreateColumn(
    'gongjongNm',
    '공정명',
    'left',
    200,
    false,
    false,
    false,
    'text',
    { flex: 3, minWidth: 80 }
  ),
  CreateColumn(
    'gongjongCd',
    '공정코드',
    'left',
    120,
    false,
    false,
    false,
    'text',
    { flex: 2, minWidth: 80 }
  ),
];

/**
 * 공정 선택 모달 (GON_BASIC 페이지와 동일: 부서 목록 → 공정 목록 선택)
 * - 왼쪽: 부서 목록 (GetDepLast)
 * - 오른쪽: 선택 부서의 공정 목록 (GetGonBasicR1)
 * - 선택 시 onSelect({ gongjongCd, gongjongNm }) 호출
 * - allowEmptySelection: true이면 「선택 비우기」로 공정 코드·명을 빈 값으로 확정 가능
 * - allowOtherOption: true이면 「기타 선택」으로 공정코드 '0'·표시명 「기타」 확정
 * - subtitle / otherOptionAlertDetail: 화면별 상단·기타 안내 문구 (미지정 시 기본값)
 */
function GongjongSelectModal({
  open,
  onClose,
  onSelect,
  custCd: custCdProp,
  allowEmptySelection = false,
  allowOtherOption = false,
  subtitle: subtitleProp,
  otherOptionAlertDetail,
}) {
  const searchInfo = useSelector((state) => state.SearchInfo);
  const custCd = custCdProp ?? searchInfo?.custCd ?? '';

  const depGridRef = useRef();
  const gongjongGridRef = useRef();
  const [depList, setDepList] = useState([]);
  const [gongjongList, setGongjongList] = useState([]);
  const [selectedDep, setSelectedDep] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDepList = async () => {
    if (!custCd) return;
    setLoading(true);
    try {
      const params = mapParamsBySchema(GON_BASIC_Schema.mapper.search1, {
        search: { ...searchInfo, custCd },
      });
      const res = await apiRequest(
        'post',
        Global.urls.Utility.getDepLast,
        params
      );
      const raw = res?.Data ?? res?.data ?? res;
      const list = Array.isArray(raw) ? raw : [];
      const mapped = list.map((r, i) => ({
        no: r.no ?? i + 1,
        depCd: r.DepCd ?? r.depCd ?? '',
        depNmLast:
          r.DepNmLast ??
          r.depNmLast ??
          r.DepNm4 ??
          r.DepNm3 ??
          r.DepNm2 ??
          r.DepNm1 ??
          '',
      }));
      setDepList(mapped);
      setSelectedDep(null);
      setGongjongList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGongjongList = async (depCd) => {
    if (!depCd) {
      setGongjongList([]);
      return;
    }
    setLoading(true);
    try {
      const params = mapParamsBySchema(GON_BASIC_Schema.mapper.search2, {
        depCd,
      });
      const res = await apiRequest(
        'post',
        Global.urls.Gongjong.getGonBasicR1,
        params
      );
      const raw = res?.Data ?? res?.data ?? res;
      const list = Array.isArray(raw) ? raw : [];
      const mapped = list.map((r, i) => ({
        no: r.no ?? i + 1,
        gongjongCd: r.GongjongCd ?? r.gongjongCd ?? '',
        gongjongNm: r.GongjongNm ?? r.gongjongNm ?? '',
      }));
      setGongjongList(mapped);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && custCd) {
      fetchDepList();
    } else if (!open) {
      setDepList([]);
      setGongjongList([]);
      setSelectedDep(null);
    }
  }, [open, custCd]);

  const handleDepSelection = () => {
    const api = depGridRef?.current?.api;
    const nodes = api?.getSelectedNodes() ?? [];
    const row = nodes[0]?.data;
    if (row) {
      setSelectedDep(row);
      fetchGongjongList(row.depCd);
    } else {
      setSelectedDep(null);
      setGongjongList([]);
    }
  };

  const handleSelect = () => {
    const api = gongjongGridRef?.current?.api;
    const nodes = api?.getSelectedNodes() ?? [];
    const row = nodes[0]?.data;
    if (row?.gongjongCd != null || row?.gongjongNm != null) {
      onSelect?.({
        gongjongCd: row.gongjongCd ?? '',
        gongjongNm: row.gongjongNm ?? '',
      });
      onClose?.();
    }
  };

  const handleClearSelection = () => {
    onSelect?.({ gongjongCd: '', gongjongNm: '' });
    onClose?.();
  };

  /** 목록에 없을 때: 공정코드는 '0', 표시명은 「기타」(저장·조회 시 서버와 맞춤) */
  const handleSelectOther = () => {
    onSelect?.({ gongjongCd: '0', gongjongNm: '기타' });
    onClose?.();
  };

  const handleClose = () => {
    setDepList([]);
    setGongjongList([]);
    setSelectedDep(null);
    onClose?.();
  };

  const GetHeaderSubtitle = () => {
    if (subtitleProp != null && String(subtitleProp).trim() !== '') {
      return subtitleProp;
    }
    if (allowOtherOption && allowEmptySelection) {
      return '부서를 고른 뒤 공정을 선택합니다. 공정을 비울 때는 「선택 비우기」를 사용합니다.';
    }
    if (allowOtherOption) {
      return '부서를 고른 뒤 공정을 선택합니다. 목록에 없으면 하단 「기타 선택」을 이용해 주세요.';
    }
    if (allowEmptySelection) {
      return '부서를 선택한 후 공정을 고르거나, 「선택 비우기」로 공정을 비울 수 있습니다.';
    }
    return '부서를 선택한 후 공정을 선택하세요';
  };

  const header = (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        whiteSpace: 'nowrap',
        bgcolor: 'white',
        px: 3,
        py: 1.5,
        flexShrink: 0,
        zIndex: 20,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          color: '#111418',
          flex: 1,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(19, 127, 236, 0.1)',
            color: 'primary.main',
            borderRadius: 1.5,
          }}
        >
          <BuildIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              color: '#111418',
              fontSize: 22,
              fontWeight: 'bold',
              letterSpacing: -0.15,
              mb: 0.5,
            }}
          >
            공정 선택
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#617589',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            {GetHeaderSubtitle()}
          </Typography>
        </Box>
      </Box>
      <IconButton
        onClick={handleClose}
        size="small"
        sx={{
          color: '#617589',
          '&:hover': {
            color: '#111418',
            bgcolor: 'transparent',
          },
        }}
      >
        <CloseIcon />
      </IconButton>
    </Paper>
  );

  const footer = (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1.5,
        px: 3,
        py: 2,
        bgcolor: 'white',
        flexShrink: 0,
      }}
    >
      <Button
        onClick={handleClose}
        variant="outlined"
        sx={{
          minWidth: '100px',
          color: '#617589',
          borderColor: 'transparent',
          fontWeight: 500,
          bgcolor: 'rgba(0, 0, 0, 0.04)',
          '&:hover': {
            bgcolor: 'rgba(0, 0, 0, 0.08)',
            borderColor: 'transparent',
          },
        }}
      >
        취소
      </Button>
      {allowEmptySelection ? (
        <Button
          onClick={handleClearSelection}
          variant="outlined"
          color="warning"
          sx={{
            minWidth: '100px',
            fontWeight: 500,
          }}
        >
          선택 비우기
        </Button>
      ) : null}
      {allowOtherOption ? (
        <Button
          onClick={handleSelectOther}
          variant="outlined"
          color="primary"
          sx={{ minWidth: '120px', fontWeight: 600 }}
        >
          기타 선택
        </Button>
      ) : null}
      <Button
        onClick={handleSelect}
        variant="contained"
        sx={{
          minWidth: '100px',
          fontWeight: 500,
          backgroundColor: '#1976d2',
          boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
          '&:hover': {
            backgroundColor: '#1565c0',
            boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
          },
        }}
      >
        선택
      </Button>
    </Paper>
  );

  return (
    <>
      {open && (
        <Dialog
          open={open}
          onClose={handleClose}
          title={header}
          titleSx={{ p: 0 }}
          maxWidth="lg"
          fullWidth
          useCloseButton={false}
          useSaveButton={false}
          actions={footer}
          actionsSx={{ p: 0 }}
          PaperProps={{
            sx: {
              borderRadius: 0,
              overflow: 'hidden',
              maxHeight: '90vh',
              height: 'auto',
            },
          }}
          contentSx={{
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            overflow: 'hidden',
            maxHeight: '90vh',
          }}
          content={
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                bgcolor: '#f6f7f8',
                p: 2,
                gap: 2,
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1' },
              }}
            >
              {allowOtherOption ? (
                <Alert
                  severity="info"
                  sx={{ flexShrink: 0, alignItems: 'flex-start' }}
                >
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                  >
                    공정 목록에 없는 사항일 경우 하단의 「기타 선택」 버튼을
                    눌러 주세요.
                  </Typography>
                  {otherOptionAlertDetail ?? (
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ lineHeight: 1.6 }}
                    >
                      「기타 선택」을 하신 뒤에는 그리드의{' '}
                      <strong>발생장소</strong> 칸에 구체적인 설명을 반드시
                      입력해 주세요.
                    </Typography>
                  )}
                </Alert>
              ) : null}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flex: 1,
                  minHeight: 420,
                  overflow: 'hidden',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#ffffff',
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: 'rgba(19, 127, 236, 0.06)',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: '#137fec',
                        fontWeight: 600,
                        fontSize: 14,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      부서 목록
                    </Typography>
                  </Box>
                  <Box
                    className="ag-theme-alpine no-scroll"
                    sx={{ flex: 1, minHeight: 0, width: '100%' }}
                  >
                    <AgGridReact
                      ref={depGridRef}
                      columnDefs={depColumnDefs}
                      rowData={depList}
                      rowSelection="single"
                      onSelectionChanged={handleDepSelection}
                      onRowClicked={handleDepSelection}
                      getRowStyle={(e) => GridFunctions.getRowStyle(e)}
                      defaultColDef={{ resizable: true, sortable: true }}
                      loading={loading}
                    />
                  </Box>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#ffffff',
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: 'rgba(19, 127, 236, 0.06)',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: '#137fec',
                        fontWeight: 600,
                        fontSize: 14,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {selectedDep
                        ? `${selectedDep.depNmLast || '부서'} 공정 목록`
                        : '공정 목록 (부서 선택 후 표시)'}
                    </Typography>
                  </Box>
                  <Box
                    className="ag-theme-alpine no-scroll"
                    sx={{ flex: 1, minHeight: 0, width: '100%' }}
                  >
                    <AgGridReact
                      ref={gongjongGridRef}
                      columnDefs={gongjongColumnDefs}
                      rowData={gongjongList}
                      rowSelection="single"
                      getRowStyle={(e) => GridFunctions.getRowStyle(e)}
                      defaultColDef={{ resizable: true, sortable: true }}
                      loading={loading}
                    />
                  </Box>
                </Paper>
              </Box>
            </Box>
          }
        />
      )}
    </>
  );
}

export default GongjongSelectModal;
